import { useEffect, useState } from "react";

const BAR_COUNT = 56;

// Shown while the real peaks decode, or if decoding/CORS fails — a calm
// placeholder silhouette rather than a broken empty strip.
const FALLBACK_PEAKS: number[] = Array.from({ length: BAR_COUNT }, (_, i) =>
  Math.min(
    1,
    0.2 + Math.abs(Math.sin(i * 0.7) + Math.sin(i * 0.23) * 0.6) * 0.6
  )
);

// Decoded envelopes are stable per URL — decode once, reuse across mounts.
const peaksCache = new Map<string, number[]>();

let sharedAudioContext: AudioContext | null = null;
function getAudioContext(): AudioContext {
  if (sharedAudioContext === null) sharedAudioContext = new AudioContext();
  return sharedAudioContext;
}

async function computePeaks(url: string): Promise<number[]> {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  const decoded = await getAudioContext().decodeAudioData(arrayBuffer);
  const channel = decoded.getChannelData(0);
  const bucketSize = Math.floor(channel.length / BAR_COUNT);
  if (bucketSize === 0) return FALLBACK_PEAKS;

  const peaks: number[] = [];
  let max = 0;
  for (let bar = 0; bar < BAR_COUNT; bar++) {
    let peak = 0;
    const start = bar * bucketSize;
    for (let i = 0; i < bucketSize; i++) {
      const amplitude = Math.abs(channel[start + i]);
      if (amplitude > peak) peak = amplitude;
    }
    peaks.push(peak);
    if (peak > max) max = peak;
  }
  return max === 0 ? peaks : peaks.map((peak) => peak / max);
}

/**
 * Normalized amplitude envelope (0..1, {@link BAR_COUNT} buckets) of the audio
 * at `url`, decoded via the Web Audio API so the waveform reflects the real
 * track rather than a decorative pattern. Returns a placeholder while decoding
 * or when the file can't be fetched/decoded (e.g. missing CORS headers).
 */
export function useAudioWaveform(url: string | null): number[] {
  const [peaks, setPeaks] = useState<number[]>(FALLBACK_PEAKS);

  useEffect(() => {
    if (url === null || url === "") {
      setPeaks(FALLBACK_PEAKS);
      return;
    }
    const cached = peaksCache.get(url);
    if (cached !== undefined) {
      setPeaks(cached);
      return;
    }
    let cancelled = false;
    setPeaks(FALLBACK_PEAKS);
    computePeaks(url)
      .then((computed) => {
        peaksCache.set(url, computed);
        if (!cancelled) setPeaks(computed);
      })
      .catch(() => {
        if (!cancelled) setPeaks(FALLBACK_PEAKS);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return peaks;
}
